import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { parse } from '@fint/money';
import { Landmark, Pencil, Plus, Sparkles, Trash2 } from 'lucide-react';
import { api, ApiError, qs } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { useToday } from '@/hooks/useOrgSettings';
import {
  Button, Card, CardContent, EmptyState, ErrorState, HBars, Input, Money, SelectField, Sheet, SheetBody, SheetFooter, SheetForm, SheetHeader, Textarea, moneyText,
} from '@/features/common/ui';
import type { ConsolidationView, DebtRow, DebtView, PlanView } from './types';

const KIND_LABEL: Record<string, string> = { loan: 'Loan', card: 'Credit card', overdraft: 'Overdraft', gold: 'Gold loan', personal: 'Personal loan', other: 'Other' };

/**
 * Debt: what the business owes, what each costs a month, and the cheapest
 * order to clear it. Avalanche (dearest rate first) against snowball
 * (smallest first) against paying only what is due, for any extra a month;
 * and a consolidation offer tested with its fee, the GST on the fee and any
 * prepayment charge in. Fint names no lender and moves no money.
 */
export function DebtTab() {
  const org = useActiveOrg();
  const queryClient = useQueryClient();
  const [extraText, setExtraText] = useState('5000');
  const [extra, setExtra] = useState('5000');
  const [editing, setEditing] = useState<DebtRow | 'new' | null>(null);
  useEffect(() => {
    const t = setTimeout(() => setExtra(/^\d+(\.\d{1,2})?$/.test(extraText.trim()) ? extraText.trim() : '0'), 400);
    return () => clearTimeout(t);
  }, [extraText]);

  const view = useQuery({ queryKey: ['cfo-debts', org?.id, extra], queryFn: () => api.get<DebtView>(`/cfo/debts${qs({ extra })}`), enabled: !!org?.id, placeholderData: (prev) => prev });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['cfo-debts', org?.id] });
  };
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/cfo/debts/${id}`), onSuccess: refresh });
  const claude = useMutation({ mutationFn: () => api.post(`/cfo/debts/claude${qs({ extra })}`), onSuccess: refresh });

  if (view.isError) return <Card><ErrorState error={view.error} onRetry={() => view.refetch()} /></Card>;
  if (!view.data) return <div className="skeleton h-48 rounded-lg" />;
  const data = view.data;
  const cur = data.currency;
  const minor = (v: string) => parse(v, cur).minor;
  const waiting = data.ai?.status === 'queued' || data.ai?.status === 'claimed';

  return (
    <div className="space-y-6">
      {data.debts.length === 0 ? (
        <Card>
          <EmptyState
            icon={Landmark}
            title="No debts entered"
            description="Add each loan, card or credit line with its rate, and Fint works out what it costs and the cheapest order to clear it."
            action={
              data.canEdit ? (
                <Button onClick={() => setEditing('new')} data-debt-add>
                  <Plus className="size-4" aria-hidden /> Add a debt
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <>
          <section className="grid gap-4 sm:grid-cols-3" aria-label="What you owe">
            <Card><CardContent className="pt-4"><p className="text-sm text-muted-foreground">Owed in all</p><p className="text-2xl font-semibold"><Money value={data.totals.outstanding} currency={cur} /></p></CardContent></Card>
            <Card><CardContent className="pt-4"><p className="text-sm text-muted-foreground">Interest a month</p><p className="text-2xl font-semibold"><Money value={data.totals.monthlyInterest} currency={cur} /></p></CardContent></Card>
            <Card><CardContent className="pt-4"><p className="text-sm text-muted-foreground">Paid a month</p><p className="text-2xl font-semibold"><Money value={data.totals.monthlyPayment} currency={cur} /></p></CardContent></Card>
          </section>

          <Card>
            <CardContent className="space-y-3 pt-4">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold">Each debt, dearest first</h2>
                {data.canEdit ? (
                  <Button size="sm" variant="outline" onClick={() => setEditing('new')} data-debt-add>
                    <Plus className="size-4" aria-hidden /> Add
                  </Button>
                ) : null}
              </div>
              <HBars
                rows={[...data.debts].sort((a, b) => Number(b.annualRate) - Number(a.annualRate)).map((d) => ({ id: d.id, name: `${d.lender} · ${Number(d.annualRate)}%`, colour: Number(d.annualRate) >= 30 ? 'red' : Number(d.annualRate) >= 15 ? 'amber' : 'teal', value: minor(d.outstanding) }))}
                currency={cur}
                title="Owed, by lender and rate"
                summary={`${moneyText(minor(data.totals.outstanding), cur)} owed across ${data.debts.length} debts.`}
                valueLabel="Owed"
                empty={{ title: 'No debts' }}
              />
              <ul className="divide-y divide-border text-sm">
                {[...data.debts].sort((a, b) => Number(b.annualRate) - Number(a.annualRate)).map((d) => (
                  <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 py-2" data-debt={d.lender}>
                    <div className="min-w-0">
                      <p className="font-medium">
                        {d.lender} <span className="text-xs text-muted-foreground">{KIND_LABEL[d.kind] ?? d.kind} · {Number(d.annualRate)}% a year {d.rateType}</span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Interest about <Money value={d.monthlyInterest} currency={cur} /> a month · paying <Money value={d.payment} currency={cur} />
                        {d.paymentAssumed ? ' (assumed - enter the EMI)' : ''} ·{' '}
                        {d.payoff.clears ? (
                          <>
                            clear in {d.payoff.months} months, <Money value={d.payoff.interest} currency={cur} /> interest
                          </>
                        ) : (
                          'this payment never clears it'
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Money value={d.outstanding} currency={cur} className="font-semibold" />
                      {data.canEdit ? (
                        <>
                          <Button size="icon" variant="ghost" aria-label={`Change ${d.lender}`} onClick={() => setEditing(d)}>
                            <Pencil className="size-4" aria-hidden />
                          </Button>
                          <Button size="icon" variant="ghost" aria-label={`Remove ${d.lender}`} onClick={() => remove.mutate(d.id)}>
                            <Trash2 className="size-4" aria-hidden />
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {data.plans ? (
            <Card data-debt-plans>
              <CardContent className="space-y-3 pt-4">
                <h2 className="text-sm font-semibold">Which to pay off first</h2>
                <Input label="Extra you could pay each month" inputMode="decimal" value={extraText} onChange={(e) => setExtraText(e.target.value)} hint="On top of each debt's own payment." />
                <div className="grid gap-3 sm:grid-cols-3">
                  <PlanCard title="Only what is due" plan={data.plans.minimum} currency={cur} />
                  <PlanCard title="Avalanche: dearest rate first" plan={data.plans.avalanche} currency={cur} base={data.plans.minimum} best />
                  <PlanCard title="Snowball: smallest first" plan={data.plans.snowball} currency={cur} base={data.plans.minimum} />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => claude.mutate()} loading={claude.isPending} disabled={waiting}>
                    <Sparkles className="size-4" aria-hidden /> {waiting ? 'Waiting for Claude' : 'Ask Claude to explain the plan'}
                  </Button>
                </div>
                {data.ai?.text ? (
                  <div className="space-y-1 rounded-md border border-border px-3 py-2 text-sm">
                    <p className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
                      <Sparkles className="size-3" aria-hidden /> Claude
                    </p>
                    <p className="whitespace-pre-wrap">{data.ai.text}</p>
                  </div>
                ) : null}
                {data.ai?.status === 'failed' ? <p className="text-xs text-destructive">Claude could not explain it: {data.ai.error}</p> : null}
              </CardContent>
            </Card>
          ) : null}

          <Consolidation debts={data.debts} currency={cur} />
        </>
      )}

      <Card>
        <CardContent className="space-y-2 pt-4 text-sm">
          <h2 className="font-semibold">Typical rates, for comparison only</h2>
          <ul className="space-y-0.5">
            {data.market.rows.map((r) => (
              <li key={r.kind} className="flex justify-between gap-3">
                <span>{r.kind}</span>
                <span className="text-muted-foreground">{r.range}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground">As of {data.market.asOf}. Fint does not recommend a lender or a product.</p>
        </CardContent>
      </Card>

      {editing ? <DebtSheet debt={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} /> : null}
    </div>
  );
}

function PlanCard({ title, plan, currency, base, best }: { title: string; plan: PlanView; currency: string; base?: PlanView; best?: boolean }) {
  const saved = base && base.clears && plan.clears ? base.interestMinor - plan.interestMinor : null;
  const months = base && base.months !== null && plan.months !== null ? base.months - plan.months : null;
  return (
    <div className={best ? 'rounded-lg border-2 border-primary/40 p-3' : 'rounded-lg border border-border p-3'} data-plan={plan.strategy}>
      <p className="text-xs font-medium text-muted-foreground">{title}</p>
      {plan.clears ? (
        <>
          <p className="text-lg font-semibold">{plan.months} months</p>
          <p className="text-xs">
            Interest <Money value={plan.interest} currency={currency} />
          </p>
          {saved !== null && saved > 0 ? (
            <p className="text-xs text-success">
              Saves {moneyText(saved, currency)}
              {months ? ` and ${months} months` : ''}
            </p>
          ) : null}
          <p className="mt-1 text-xs text-muted-foreground">Order: {plan.order.map((o) => o.name).join(' → ')}</p>
        </>
      ) : (
        <p className="text-sm text-destructive">Never clears at these payments.</p>
      )}
    </div>
  );
}

function Consolidation({ debts, currency }: { debts: DebtRow[]; currency: string }) {
  const [chosen, setChosen] = useState<string[]>(() => debts.filter((d) => Number(d.annualRate) >= 18).map((d) => d.id));
  const [rate, setRate] = useState('14');
  const [tenure, setTenure] = useState('24');
  const [fee, setFee] = useState('0');
  const [prepayment, setPrepayment] = useState('0');
  const run = useMutation({
    mutationFn: () => api.post<ConsolidationView>('/cfo/debts/consolidate', { debtIds: chosen, annualRate: Number(rate), tenureMonths: Number(tenure), fee: fee || '0', prepaymentPercent: Number(prepayment || 0) }),
  });
  const r = run.data;
  const error = run.error instanceof ApiError ? run.error.message : null;
  return (
    <Card data-debt-consolidation>
      <CardContent className="space-y-3 pt-4 text-sm">
        <h2 className="font-semibold">Test a consolidation offer</h2>
        <fieldset className="space-y-1">
          <legend className="text-xs text-muted-foreground">Debts it would replace</legend>
          {debts.map((d) => (
            <label key={d.id} className="flex items-center gap-2 touch:min-h-11">
              <input type="checkbox" checked={chosen.includes(d.id)} onChange={(e) => setChosen((c) => (e.target.checked ? [...c, d.id] : c.filter((id) => id !== d.id)))} />
              {d.lender} · <Money value={d.outstanding} currency={currency} /> at {Number(d.annualRate)}%
            </label>
          ))}
        </fieldset>
        <div className="grid gap-3 sm:grid-cols-4">
          <Input label="Offer rate, % a year" inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} />
          <Input label="Months" inputMode="numeric" value={tenure} onChange={(e) => setTenure(e.target.value)} />
          <Input label="Processing fee" inputMode="decimal" value={fee} onChange={(e) => setFee(e.target.value)} hint="Before GST." />
          <Input label="Prepayment charge, %" inputMode="decimal" value={prepayment} onChange={(e) => setPrepayment(e.target.value)} hint="On the debts closed." />
        </div>
        <Button size="sm" onClick={() => run.mutate()} loading={run.isPending} disabled={chosen.length === 0} data-debt-consolidate>
          Work it out
        </Button>
        {error ? <p className="text-destructive">{error}</p> : null}
        {r ? (
          <div className="space-y-1 rounded-md bg-muted/50 px-3 py-2" data-consolidation-result>
            <p className="font-medium">
              {r.saving === null ? 'The debts it replaces never clear at their current payments, so any plan that clears them is better.' : Number(r.saving) > 0 ? <>Saves about <Money value={r.saving} currency={currency} /> in all.</> : <>Costs about <Money value={String(-Number(r.saving))} currency={currency} /> more in all.</>}
            </p>
            <p>
              New EMI <Money value={r.emi} currency={currency} /> for {r.tenureMonths} months (now paying <Money value={r.oldMonthlyPayment} currency={currency} /> a month), interest <Money value={r.newInterest} currency={currency} />
              {r.oldInterest ? <> against <Money value={r.oldInterest} currency={currency} /> on the old debts{r.oldMonths ? ` over ${r.oldMonths} months` : ''}</> : null}.
            </p>
            <p>
              Upfront: fee <Money value={r.fee} currency={currency} /> + GST on it <Money value={r.feeGst} currency={currency} /> + prepayment <Money value={r.prepayment} currency={currency} />. Real cost with the fee in: {r.effectiveAnnualRate}% a year.
              {r.breakEvenMonth ? ` The fee is paid back by month ${r.breakEvenMonth}.` : ' The fee is never paid back by lower interest.'}
            </p>
            <p className="text-xs text-muted-foreground">{r.prepaymentNote}</p>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function DebtSheet({ debt, onClose, onSaved }: { debt: DebtRow | null; onClose: () => void; onSaved: () => void }) {
  const today = useToday();
  const [kind, setKind] = useState(debt?.kind ?? 'card');
  const [lender, setLender] = useState(debt?.lender ?? '');
  const [outstanding, setOutstanding] = useState(debt?.outstanding ?? '');
  const [asOf, setAsOf] = useState(debt?.asOf ?? today);
  const [annualRate, setAnnualRate] = useState(debt ? String(Number(debt.annualRate)) : '');
  const [emi, setEmi] = useState(debt?.emi ?? '');
  const [endDate, setEndDate] = useState(debt?.endDate ?? '');
  const [rateType, setRateType] = useState(debt?.rateType ?? 'fixed');
  const [note, setNote] = useState(debt?.note ?? '');
  const save = useMutation({
    mutationFn: () => {
      const body = { kind, lender, outstanding: outstanding.trim(), asOf, annualRate: Number(annualRate), emi: emi.trim() || null, endDate: endDate || null, rateType, note: note.trim() || null };
      return debt ? api.put(`/cfo/debts/${debt.id}`, body) : api.post('/cfo/debts', body);
    },
    onSuccess: onSaved,
  });
  const error = save.error instanceof ApiError ? save.error : undefined;
  const title = debt ? `Change ${debt.lender}` : 'Add a debt';
  return (
    <Sheet label={title} onClose={onClose}>
      <SheetHeader title={title} onClose={onClose} />
      <SheetForm
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <SheetBody>
          <SelectField id="debt-kind" label="Kind" value={kind} onChange={setKind}>
            {Object.entries(KIND_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </SelectField>
          <Input label="Owed to" required value={lender} onChange={(e) => setLender(e.target.value)} maxLength={120} error={error?.fieldError('lender')} />
          <Input label="Owed today" required inputMode="decimal" value={outstanding} onChange={(e) => setOutstanding(e.target.value)} error={error?.fieldError('outstanding')} />
          <Input label="As of" type="date" required value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          <Input label="Interest, % a year" required inputMode="decimal" value={annualRate} onChange={(e) => setAnnualRate(e.target.value)} hint="A card is usually 36-45%." error={error?.fieldError('annualRate')} />
          <Input label="Monthly payment (EMI or card minimum)" inputMode="decimal" value={emi} onChange={(e) => setEmi(e.target.value)} hint="Leave empty for a card: 5% of the balance is assumed." error={error?.fieldError('emi')} />
          <Input label="Ends on" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          <SelectField id="debt-rate-type" label="Rate" value={rateType} onChange={setRateType}>
            <option value="fixed">Fixed</option>
            <option value="floating">Floating</option>
          </SelectField>
          <Textarea label="Note" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
          {error && !error.errors?.length ? (
            <p role="alert" className="text-sm text-destructive">
              {error.message}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter className="justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending} disabled={!lender || !outstanding || !annualRate} data-debt-save>
            {debt ? 'Save changes' : 'Add debt'}
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}
